import React, { useState, useEffect } from 'react';
import './Leaderboard.css';
import { API_URL } from '../utils/networkConfig';
import { IoTrophyOutline, IoCalendarOutline, IoHomeOutline, IoVideocamOutline, IoRibbonOutline } from 'react-icons/io5';

function Leaderboard() {
  const [homeScores, setHomeScores] = useState([]);
  const [studioScores, setStudioScores] = useState([]);
  const [allTimeScores, setAllTimeScores] = useState([]);
  const [activeTab, setActiveTab] = useState('home'); // 'home', 'studio', or 'alltime'
  const [timePeriod, setTimePeriod] = useState('today'); // 'today' or 'alltime'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLeaderboards();
  }, [timePeriod]);

  const fetchLeaderboards = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };
      
      if (timePeriod === 'today') {
        console.log('[LEADERBOARD] Fetching daily leaderboards...');
        console.log('[LEADERBOARD] API_URL:', API_URL);
        
        // Fetch daily leaderboards for home and studio
        const [homeResponse, studioResponse] = await Promise.all([
          fetch(`${API_URL}/api/game-sessions/leaderboard-daily-home`, { headers }),
          fetch(`${API_URL}/api/game-sessions/leaderboard-daily-studio`, { headers })
        ]);
        
        console.log('[LEADERBOARD] Home response status:', homeResponse.status);
        console.log('[LEADERBOARD] Studio response status:', studioResponse.status);
        
        // Check if responses are OK before parsing JSON
        if (!homeResponse.ok || !studioResponse.ok) {
          throw new Error('Failed to fetch daily leaderboards');
        }
        
        const homeData = await homeResponse.json();
        const studioData = await studioResponse.json();
        
        console.log('[LEADERBOARD] Daily home scores:', homeData);
        console.log('[LEADERBOARD] Daily studio scores:', studioData);
        
        // Map the data to ensure consistent structure
        const mappedHomeData = Array.isArray(homeData) ? homeData.map(user => ({
          username: user.username || user.name,
          total_points: user.total_points || user.awarded_points || 0,
          role_id: user.role_id || 2
        })) : [];
        
        const mappedStudioData = Array.isArray(studioData) ? studioData.map(user => ({
          username: user.username || user.name,
          total_points: user.total_points || user.awarded_points || 0,
          role_id: user.role_id || 3
        })) : [];
        
        console.log('[LEADERBOARD] Mapped home scores:', mappedHomeData);
        console.log('[LEADERBOARD] Mapped studio scores:', mappedStudioData);
        
        setHomeScores(mappedHomeData);
        setStudioScores(mappedStudioData);
        setAllTimeScores([]); // Clear all-time scores
      } else if (timePeriod === 'alltime') {
        console.log('[LEADERBOARD] Fetching all-time leaderboards...');
        
        try {
          // Try to fetch from new separate endpoints first
          const [homeResponse, studioResponse] = await Promise.all([
            fetch(`${API_URL}/api/game-sessions/leaderboard-all-home`, { headers }),
            fetch(`${API_URL}/api/game-sessions/leaderboard-all-studio`, { headers })
          ]);
          
          if (homeResponse.ok && studioResponse.ok) {
            const homeData = await homeResponse.json();
            const studioData = await studioResponse.json();
            
            console.log('[LEADERBOARD] All-time home scores:', homeData);
            console.log('[LEADERBOARD] All-time studio scores:', studioData);
            
            // Map the data to ensure consistent structure
            const mappedHomeData = Array.isArray(homeData) ? homeData.map(user => ({
              username: user.username || user.name,
              total_points: user.total_points || 0,
              role_id: user.role_id || 2
            })) : [];
            
            const mappedStudioData = Array.isArray(studioData) ? studioData.map(user => ({
              username: user.username || user.name,
              total_points: user.total_points || 0,
              role_id: user.role_id || 3
            })) : [];
            
            console.log('[LEADERBOARD] Mapped all-time home scores:', mappedHomeData);
            console.log('[LEADERBOARD] Mapped all-time studio scores:', mappedStudioData);
            
            setHomeScores(mappedHomeData);
            setStudioScores(mappedStudioData);
            setAllTimeScores([...mappedHomeData, ...mappedStudioData]);
          } else {
            // Fallback to original endpoint and filter on frontend
            console.log('[LEADERBOARD] New endpoints not available, using fallback...');
            const response = await fetch(`${API_URL}/api/game-sessions/leaderboard-all`, { headers });
            
            if (!response.ok) {
              throw new Error('Failed to fetch all-time leaderboard');
            }
            
            const allData = await response.json();
            console.log('[LEADERBOARD] All-time data from fallback:', allData);
            
            // Filter data by role_id on frontend
            const mappedHomeData = Array.isArray(allData) ? allData
              .filter(user => user.role_id === 2)
              .map(user => ({
                username: user.username || user.name,
                total_points: user.total_points || 0,
                role_id: user.role_id || 2
              })) : [];
            
            const mappedStudioData = Array.isArray(allData) ? allData
              .filter(user => user.role_id === 3)
              .map(user => ({
                username: user.username || user.name,
                total_points: user.total_points || 0,
                role_id: user.role_id || 3
              })) : [];
            
            console.log('[LEADERBOARD] Filtered all-time home scores:', mappedHomeData);
            console.log('[LEADERBOARD] Filtered all-time studio scores:', mappedStudioData);
            
            setHomeScores(mappedHomeData);
            setStudioScores(mappedStudioData);
            setAllTimeScores([...mappedHomeData, ...mappedStudioData]);
          }
        } catch (fallbackError) {
          console.error('[LEADERBOARD] Both new endpoints and fallback failed:', fallbackError);
          throw new Error('Failed to fetch all-time leaderboards');
        }
      }
    } catch (err) {
      console.error('Leaderboard fetch error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleTimePeriodChange = (period) => {
    setTimePeriod(period);
    // Reset to home tab when changing time period
    setActiveTab('home');
  };

  const renderLeaderboardTable = (scores, type) => {
    console.log(`[RENDER] Rendering ${type} leaderboard with scores:`, scores);
    
    if (scores.length === 0) {
      const message = timePeriod === 'today' 
        ? `No scores recorded for today yet from ${type === 'home' ? 'home players' : 'studio contestants'}.`
        : `No ${type === 'home' ? 'home players' : 'studio contestants'} have scored points yet.`;
      
      return (
        <div className="no-scores">
          <p>{message}</p>
        </div>
      );
    }

    const pointsColumnHeader = timePeriod === 'today' ? 'Total Points Today' : 'All-Time Points';

    return (
      <div className="leaderboard-table-wrapper">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Player</th>
              <th>{pointsColumnHeader}</th>
            </tr>
          </thead>
          <tbody>
                        {scores.map((score, index) => {
              console.log(`[RENDER] Rendering score for ${score.username}:`, score);
              return (
                <tr key={score.username || `user-${index}`} className={index === 0 ? 'winner' : ''}>
                  <td>
                    <div className="rank-display">
                      {index === 0 && <IoRibbonOutline className="crown-icon" />}
                      #{index + 1}
                    </div>
                  </td>
                  <td className="player-name">{score.username || 'Unknown User'}</td>
                  <td className="points-display">
                    <span className="points-number">{score.total_points || 0}</span>
                    <span className="points-label">pts</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="leaderboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading leaderboards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard">
        <div className="error-container">
          <div className="error-message">{error}</div>
          <button onClick={fetchLeaderboards} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard">
      <div className="leaderboard-header">
        <h2 className="heading-with-icon"><IoTrophyOutline className="inline-icon" /> {timePeriod === 'today' ? 'Daily Leaderboards' : 'All-Time Leaderboards'}</h2>
        <p>{timePeriod === 'today' ? 'Total points earned today across all questions' : 'Lifetime total scores across all questions and sessions'}</p>
        
        {/* Time Period Filter */}
        <div className="time-period-filters">
          <button 
            className={`period-btn ${timePeriod === 'today' ? 'active' : ''}`}
            onClick={() => handleTimePeriodChange('today')}
          >
            <IoCalendarOutline className="inline-icon" /> Today's Scores
          </button>
          <button 
            className={`period-btn ${timePeriod === 'alltime' ? 'active' : ''}`}
            onClick={() => handleTimePeriodChange('alltime')}
          >
            <IoTrophyOutline className="inline-icon" /> All-Time Scores
          </button>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'home' ? 'active' : ''}`}
            onClick={() => handleTabChange('home')}
          >
            <span className="tab-icon"><IoHomeOutline className="inline-icon" /></span>
            <span className="tab-text">Scores from at home</span>
            <span className="tab-count">({homeScores.length})</span>
          </button>
          <button 
            className={`tab ${activeTab === 'studio' ? 'active' : ''}`}
            onClick={() => handleTabChange('studio')}
          >
            <span className="tab-icon"><IoVideocamOutline className="inline-icon" /></span>
            <span className="tab-text">Scores from the studio</span>
            <span className="tab-count">({studioScores.length})</span>
          </button>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 'home' && (
          <div className="tab-panel home-panel">
            <div className="panel-header">
              <h3 className="heading-with-icon"><IoHomeOutline className="inline-icon" /> Players at Home</h3>
              <p>{timePeriod === 'today' ? 'Regular users playing from home today' : 'All-time home player rankings'}</p>
            </div>
            {renderLeaderboardTable(homeScores, 'home')}
          </div>
        )}

        {activeTab === 'studio' && (
          <div className="tab-panel studio-panel">
            <div className="panel-header">
              <h3 className="heading-with-icon"><IoVideocamOutline className="inline-icon" /> Studio Contestants</h3>
              <p>{timePeriod === 'today' ? 'Game show contestants in the studio today' : 'All-time studio contestant rankings'}</p>
            </div>
            {renderLeaderboardTable(studioScores, 'studio')}
          </div>
        )}
      </div>
    </div>
  );
}

export default Leaderboard; 